import { ReadonlyURLSearchParams } from "next/navigation";
import { parse } from "qs";

import { leaflyShopStringify } from "./qs";

/**
 * Utility for updating a URL with new query string values by creating an updated URLSearchParams hash
 *
 * @param {URLSearchParams} searchParams base query string hash to start with
 * @param {string} name name of search param to toggle
 * @param {string} value value to toggle for param name
 * @param {object} options options / config for query string serialization
 * @param {boolean} options.forceArray ensures the resulting QS key/value is serialized as an array
 * @returns {URLSearchParams} updated search param object
 */
const toggleURLSearchParamValue = (
  searchParams: ReadonlyURLSearchParams | URLSearchParams | null,
  name: string,
  value: string,
  { forceArray } = { forceArray: false },
) => {
  const bracketedQSKey = `${name}[]`;
  let qsKey = forceArray ? bracketedQSKey : name;
  let appliedValues: string[] = [];
  // * Due to our usage of leaflyShopStringify, search param values might appear as arrays with bracketed syntax.
  // * If/when we deprecate our usage of bracketed query string parameters on the frontend, the below logic can also be deprecated.
  if (searchParams) {
    if (searchParams.getAll(name).length > 0) {
      qsKey = name;
    }
    if (searchParams.getAll(bracketedQSKey).length > 0) {
      qsKey = bracketedQSKey;
    }
    appliedValues = searchParams.getAll(qsKey);
  }

  // * Clone searchParams as the passed in version might be ReadonlyURLSearchParams (from next/navigation)
  const updatedParams = new URLSearchParams(searchParams?.toString());
  if (appliedValues.includes(value)) {
    updatedParams.delete(qsKey, value);
  } else {
    updatedParams.append(qsKey, value);
  }

  // * More patching for leaflyShopStringify backwards compatibility.
  return reformatParams(updatedParams);
};

/**
 * Reformat a URLSearchParams instance to use RFC1738 encoding and bracket-style arrays.
 */
function reformatParams(params: URLSearchParams): URLSearchParams {
  const parsedQueryString = parse(params.toString());
  const reformattedQueryString = leaflyShopStringify(parsedQueryString);
  return new URLSearchParams(reformattedQueryString);
}

export default toggleURLSearchParamValue;
